











































































import Connection from '@/models/Connection';
import Node from '@/models/Node';
import { PacketType } from '@/models/Packet';
import StringPacking from '@/models/StringPacking';
import NodeRequests from '@/networking/Node';
import { SendPacket } from '@/networking/SendPacket';
import Mutations from '@/store/mutations';
import Vue from 'vue';
import Component from 'vue-class-component';
import { Emit, Prop, Watch } from 'vue-property-decorator';

enum DocumentType
{
    WikipediaArticle,
    Text
}

type ExtractedData = { events: { id: number, title: string, description: string, start: string }[], connections: { a: number, b: number, description: string }[] };

@Component
export default class DocumentImport extends Vue 
{
    @Prop(Number) readonly timelineid!: number;

    // When this is unloaded, kill the import
    beforeDestroy()
    {
        this.hasUnloaded = true;
    }

    DocumentType = DocumentType;
    selectedType = DocumentType.WikipediaArticle;

    hasUnloaded = false;

    targetUrl = "";
    targetText = "";
    importing = false;
    importProgress = 0;

    get shouldDisplayProgress()
    {
        return this.importing && this.importProgress > 0;
    }

    get isValidWikipediaURL()
    {
        return this.targetUrl.startsWith("https://en.wikipedia.org/wiki/");
    }

    async extractEventsAndConnectionsFromWikipedia(timelindId: number)
    {
        const resp = await this.$http.post<ExtractedData>(`document/import/wikipedia`, { url: this.targetUrl });
        return resp.data;
    }

    async extractEventsAndConnectionsFromText(timelindId: number)
    {
        const resp = await this.$http.post<ExtractedData>(`document/import/text`, { document_text: this.targetText });
        return resp.data;
    }

    async importArticle(timlineId: number)
    {
        let resp;
        switch(this.selectedType)
        {
        case DocumentType.WikipediaArticle:
            resp = await this.extractEventsAndConnectionsFromWikipedia(timlineId);
            break;
        case DocumentType.Text:
            resp = await this.extractEventsAndConnectionsFromText(timlineId);
            break;
        default:
            throw new Error(`Invalid document type ${this.selectedType}`);
        }

        if (this.hasUnloaded) return;

        const imported: ExtractedData = resp;
        const localIdsToRealIds = new Map<number, number>();

        console.log(`Extracted ${imported.events.length} events and ${imported.connections.length} connections from the document.  Importing...`);

        // First, create all events
        for(let i = 0; i < imported.events.length; ++i)
        {
            const target = imported.events[i];
            // alert(`Creating event ${target.title}...`);
            console.log(`Creating event ${target.title}...`);
            const startDate = new Date(target.start);
            const created = await NodeRequests.CreateNode(timlineId, target.title, startDate, startDate, target.description);
            console.log(`Created event ${target.title} with ID ${created}`);
            if (!created) throw new Error(`Failed to create imported event ${target.title}`);
            if (this.hasUnloaded) return;
            localIdsToRealIds.set(target.id, created.nodeid);
            this.importProgress = (i + 1) / imported.events.length * 50;
        }

        // Wait 0.2 seconds to allow the events and the associated components to be created
        await new Promise(resolve => setTimeout(resolve, 200));

        // Then, create connections using the mapped IDs
        for(let i = 0; i < imported.connections.length; ++i)
        {
            const target = imported.connections[i];
            const a = localIdsToRealIds.get(target.a)!;
            const b = localIdsToRealIds.get(target.b)!;
            
            console.log(`Creating connection between ${a} and ${b} w/ description ${target.description}...`);
            let conn = new Connection(a, b);
            conn.description = target.description;
            this.$store.commit(Mutations.setConnection, conn);

            SendPacket(PacketType.NodeAddConnection, {
                nodeid: a,
                otherid: b,
            });

            this.$store.commit(Mutations.updateConnectionText, conn);
            SendPacket(PacketType.UpdateConnectionText, {
                nodeone: a,
                nodetwo: b,
                value: StringPacking.Compress(target.description, '')
            });
            this.importProgress = 50 + (i + 1) / imported.connections.length * 50;
        }
        return resp;
    }

    async clickImportArticle()
    {
        this.importing = true;
        try
        {
            await this.importArticle(this.timelineid);
        }
        catch (e)
        {
            console.error(e);
            alert('Failed to import article.  See console for details.');
        }
        finally
        {
            this.importing = false;
        }
    }

    async clickImportText()
    {
        this.importing = true;
        try
        {
            await this.importArticle(this.timelineid);
        }
        catch (e)
        {
            console.error(e);
            alert('Failed to import text.  See console for details.');
        }
        finally
        {
            this.importing = false;
        }
    }
}
